import '../../../scss/pages/admin/_admin-clients.scss';

import {
  Checkbox,
  DialogActionButton,
  Radio,
  RadioListItem,
  saveCallbackStatus,
  TextField,
} from '@finpay-development/shared-components';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import { Box, Divider, Typography } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import { Form, Formik } from 'formik';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';

import { PatientPaymentProgram } from '../../../patient/components/models/patient-payment-program';
import { Payment } from '../../../patient/components/models/payment';
import { PaymentMethod, PAYMENT_METHOD_TYPES } from '../../../patient/components/models/payment-method';
import { PAYMENT_TYPES } from '../../../patient/components/models/payment-type';
import { StripePaymentMethod } from '../../../patient/components/models/stripe-payment-method';
import { setPatientMakePaymentToken, setRecurringPaymentToken } from '../../../patient/state/patient-slice';
import { addPatientPaymentMethod, getPaymentMethods, makePayment } from '../../../patient/state/patient-thunk';
import { formatNumberToUSD } from '../../../shared/calculators';
import { CustomFormikProps } from '../../../shared/model/formik-props';
import { RootState } from '../../../shared/state/root-reducer';
import { AppDispatch } from '../../../shared/state/store';
import { bankSchema } from '../../../shared/validation/schemas';
import StripeBankField from '../../../stripe/components/StripeBankField';
import StripeCardField from '../../../stripe/components/StripeCardField';
import { PaymentDetail } from '../../../patient/components/models/payment-detail';
import { Utils } from '../../../shared/utils';

interface PaymentProgressModalProps {
  open: boolean;
  handleModalClose: () => void;
}

export function PaymentProgressModal(props: PaymentProgressModalProps) {
  const { open, handleModalClose } = props;

  const [enableSaveButton, setEnableSaveButton] = useState(false);
  const [isNewSource, setIsNewSource] = useState(false);
  const [isSourceCard, setIsSourceCard] = useState(false);
  const [isStripeEmpty, setIsStripeEmpty] = useState(false);
  const [receiptForEmail, setReceiptForEmail] = useState<string>('');

  const stateFields = {
    stripeToken: useSelector(
      (state: RootState) => state.patientContext.patientMakePaymentTokens?.token
    ),
    stripeRecurringCardToken: useSelector(
      (state: RootState) => state.patientContext.recurringPaymentTokens.token
    ),
    stripeBankToken: useSelector(
      (state: RootState) => state.patientContext.patientMakePaymentTokens?.bankToken
    ),
    selectedPatient: useSelector(
      (state: RootState) => state.patientContext.selectedPatient
    ),
    currentConvertedEncounter: useSelector((state: RootState) => {
      return state.patientContext.currentConvertedEncounter;
    }),
    patientPaymentProgram: useSelector((state: RootState) => {
      return state?.patientContext?.currentConvertedEncounter?.patientPaymentProgram?.length > 0 ?
      state.patientContext.currentConvertedEncounter.patientPaymentProgram[state.patientContext.currentConvertedEncounter.patientPaymentProgram.length - 1] : {} as PatientPaymentProgram
    }),
    patientPaymentMethods: useSelector((state: RootState) => {
      return state?.patientContext?.currentConvertedEncounter?.patientPaymentMethods?.length > 0 ?
      state?.patientContext?.currentConvertedEncounter?.patientPaymentMethods
        .filter((paymentMethod: StripePaymentMethod) => (paymentMethod?.metadata?.metaData_paymentMethodType?.includes("RECURRING") || paymentMethod?.metadata?.isRecurring)
        && (paymentMethod?.object === 'bank_account' ? paymentMethod?.status === 'verified' : true)) : []
    }),
    patientPaymentMethodsLoading: useSelector((state: RootState) => {
      return state.patientContext?.isLoading?.paymentMethods
    })
  }
  const { stripeToken, stripeRecurringCardToken, stripeBankToken, currentConvertedEncounter, selectedPatient, patientPaymentProgram, patientPaymentMethods, patientPaymentMethodsLoading } = stateFields;
  const dispatch = useDispatch<AppDispatch>();

  const stripeTokenCallback = (data: {token?: string, cardToken?: string, bankToken?: string}) => {
    const {token, cardToken, bankToken} = data
    if (token && cardToken) {
      dispatch(setPatientMakePaymentToken({token: token, cardToken: cardToken}))
    } else if (bankToken) {
      dispatch(setPatientMakePaymentToken({bankToken: bankToken}))
    }
  }

  const stripeRecurringPaymentTokenCallback = (data: { token?: string, cardToken?: string, bankToken?: string }) => {
    const {token, cardToken} = data
    if (token && cardToken) {
      dispatch(setRecurringPaymentToken({token: token, cardToken: cardToken}))
    }
  }

  useEffect(() => {
    if (!patientPaymentMethodsLoading) {
      dispatch(getPaymentMethods({
        patientId: currentConvertedEncounter?.patientId,
        encounterId: currentConvertedEncounter?.patientEncounterId,
        isAccountHolder: true
      }));
    }
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    if (!open && stripeRecurringCardToken) {
      dispatch(setRecurringPaymentToken({}))
    }
  }, [dispatch, open, stripeRecurringCardToken])

  const stripeDisabledCallback = (isDisabledFromStripe: boolean ) => {
    setIsStripeEmpty(isDisabledFromStripe)
  };

  const formRef: any = useRef();

  function handleSaveCallback(saveSuccessful: boolean) {
    handleModalClose()
  }

  const getPaymentInfo = () => {
    const currentFormValues = formRef?.current?.values;
    let type;
    if (isNewSource) {
      if (isSourceCard) {
        type = PAYMENT_METHOD_TYPES.CARD
      } else {
        type = PAYMENT_METHOD_TYPES.ACH
      }
    } else {
      type = (patientPaymentMethods[parseInt(currentFormValues.paymentMethod)].object === "card") ?  PAYMENT_METHOD_TYPES.CARD : PAYMENT_METHOD_TYPES.ACH
    }
    return type;
  }

  const isWorkFlowStatusGoodStandingOrConvert = (
    (currentConvertedEncounter?.workflow?.workflowStatus.workflowStatusId === 8) || (currentConvertedEncounter?.workflow?.workflowStatus.workflowStatusId === 12)
  )

  const nextOrMissedPaymentValue = patientPaymentProgram?.patientPaymentSchedule?.missedPmts > 0 && !isWorkFlowStatusGoodStandingOrConvert ? (
    patientPaymentProgram?.patientPaymentSchedule?.missedPmts * patientPaymentProgram?.patientPaymentSchedule?.paymentDueAmt
  ) : (
    patientPaymentProgram?.patientPaymentSchedule?.paymentDueAmt
  );

  const getPaymentAmount = () => {
    const currentVals = formRef?.current?.values
    if (currentVals?.makePaymentType === 'missedPayment') {
      return nextOrMissedPaymentValue
    } else if (currentVals?.makePaymentType === 'balance') {
      return patientPaymentProgram?.patientPaymentSchedule?.pfrBalance
    } else {
      return parseFloat(currentVals?.amount)
    }
  }

  const getPaymentMethod = () => {
    const currentFormValues = formRef?.current?.values;
    let response = {};
    if (isNewSource) {
      response = {
        ...getPaymentInfo(),
        nameOnCard: currentFormValues.nameOnCard,
        externalPaymentId: isSourceCard ? stripeToken : stripeBankToken
      }
    } else {
      const paymentInfo = patientPaymentMethods[parseInt(currentFormValues.paymentMethod)] as StripePaymentMethod;
      response = {
        ...getPaymentInfo(),
        externalPaymentId: paymentInfo.id
      }
    }
    return response;
  }

  const mapToPayment = () => {
    const currentDateTime = new Date();
    const payorId = (patientPaymentMethods?.length > 0 && formRef?.current?.values?.paymentMethod) ?
    (patientPaymentMethods[parseInt(formRef?.current?.values?.paymentMethod)]?.metadata?.metaData_payorId || undefined) : undefined;

    return {
      paymentId: 0, // always 0 when we are POSTing a new payment. use paymentID for release payment
      isManual: true, // hardcoded
      isAuthExpired: false, // hardcoded
      paymentGroupId: `${patientPaymentProgram?.patientPaymentSchedule?.patientPaymentScheduleId}`,
      patientPaymentScheduleId: patientPaymentProgram?.patientPaymentSchedule?.patientPaymentScheduleId,
      patientId: currentConvertedEncounter.patientId,
      patientEncounterId: currentConvertedEncounter.patientEncounterId,
      paymentMethods: [{
        ...getPaymentMethod(),
        payorId: payorId ? parseInt(payorId) : undefined,
      }],
      paymentAmt: getPaymentAmount(),
      paymentType: PAYMENT_TYPES.SELF,
      paymentInitDt: currentDateTime.toISOString(),
      isAuthOnly: false, // this will be false because we are actually charging the cards in this case
    } as Payment;
  }

  const patientsAndChampionsList = Utils.getPatientsAndChampionsList(selectedPatient, currentConvertedEncounter, true); // payor can be anyone in this list
  const isPatient = currentConvertedEncounter?.patientChampion?.filter(x => x.isGuarantor).length <= 0;
  const isFullPayPlan = (patientPaymentProgram?.patientPaymentSchedule?.paymentFreq === 'F')
  const payorId = !isPatient && patientsAndChampionsList?.length > 1 ? patientsAndChampionsList[1].id : undefined
  const getReceiptEmail = (id: string) => {
    patientsAndChampionsList.forEach((item: {id: number, email: string}) => {
      if (item.id === parseInt(id) || patientsAndChampionsList?.length === 1) {
        setReceiptForEmail(item.email)
      }
    })
  }

  if (!receiptForEmail) {
    getReceiptEmail(payorId ? payorId.toString() : currentConvertedEncounter.patientId?.toString()); // get guarantor's email, if it exists
  }

  const mapToRecurringPayment = () => {
    const currentVals = formRef?.current?.values;

    const mappedPaymentMethods = [{
        paymentMethodId: 0,
        payorId,
        paymentAmt: 0,
        // reuse date from existing payment schedule or use today's date
        scheduleStartDt: patientPaymentProgram?.patientPaymentSchedule?.scheduleStartDt ? patientPaymentProgram?.patientPaymentSchedule?.scheduleStartDt : new Date().toISOString(),
        externalPaymentMethodId: PAYMENT_METHOD_TYPES.CARD.externalPaymentMethodId,
        paymentMethodTypeId: PAYMENT_METHOD_TYPES.CARD.paymentMethodTypeId,
        externalPaymentId: stripeRecurringCardToken,
        receiptEmail: receiptForEmail,
        nameOnCard: currentVals?.nameOnCard,
      }] as PaymentMethod[]
    const paymentType = PAYMENT_TYPES.RECURRING

    return {
      paymentMethods: mappedPaymentMethods,
      paymentType: paymentType
    } as PaymentDetail
  }

  async function handleSave() {
    const paymentInfo = mapToPayment();
    const recurringPaymentInfo = mapToRecurringPayment();
    await dispatch(makePayment({payment: paymentInfo, isAccountHolder: true}));
    // save the payment method, if applicable
    if (
      formRef?.current?.values?.saveAsRecurring &&
      !isFullPayPlan &&
      formRef?.current?.values?.paymentMethod === "newSource"
    ) {
      await dispatch(
        addPatientPaymentMethod({
          patientId: currentConvertedEncounter?.patientId,
          encounterId: currentConvertedEncounter?.patientEncounterId,
          paymentDetail: recurringPaymentInfo,
          isAccountHolder: true,
        })
      );
    }
    await dispatch(getPaymentMethods({patientId: currentConvertedEncounter.patientId, encounterId: currentConvertedEncounter.patientEncounterId, isAccountHolder: true}))
    handleModalClose();
  }

  const initialValues = {
    makePaymentType: "missedPayment", // one of 'missedPayment', 'balance', 'other'
    amount: null,
    isNewSourceCreditCard: true,
    saveAsRecurring: isFullPayPlan ? false : true, // full pay plans can't have a recurring payment. Recurring checkbox won't show up either.
    payorName: "",
    paymentMethod: patientPaymentMethods?.length > 0 ? "0" : "newSource", // either 'newSource' or "0", "1", "2", "3" ... etc
    nameOnCard: "",
    bankName: "",
    routingNumber: "",
    accountNumber: "",
    retypeAccountNumber: ""
  };

  const pfrBalance = patientPaymentProgram?.patientPaymentSchedule?.pfrBalance

  const cardValidationSchema = {
    makePaymentType: Yup.string().min(1),
    amount: Yup.number()
      .typeError('Amount must be a number')
      .min(.50, 'Minimum payment is 50 cents')
      .max(pfrBalance, `You cannot make a payment greater than the remaining balance of ${formatNumberToUSD(pfrBalance)}`)
      .nullable(true)
      .transform((_, val) => val ? Number(val) : null),
    isNewSourceCreditCard: Yup.boolean(),
    nameOnCard: (isNewSource && isSourceCard) ? Yup.string().min(1) : Yup.string(), // required when useExistingSource === false.
  }

  const bankValidationSchema = {
    makePaymentType: Yup.string().min(1),
    amount: Yup.number()
      .typeError('Amount must be a number')
      .min(.50, 'Minimum payment is 50 cents')
      .max(pfrBalance, `You cannot make a payment greater than the remaining balance of ${formatNumberToUSD(pfrBalance)}`)
      .nullable(true)
      .transform((_, val) => val ? Number(val) : null),
    isNewSourceCreditCard: Yup.boolean(),
    ...bankSchema
  }

  const existingPaymentSchema = {
    makePaymentType: Yup.string().min(1),
    amount: Yup.number()
      .typeError('Amount must be a number')
      .min(.50, 'Minimum payment is 50 cents')
      .max(pfrBalance, `You cannot make a payment greater than the remaining balance of ${formatNumberToUSD(pfrBalance)}`)
      .nullable(true)
      .transform((_, val) => val ? Number(val) : null),
    isNewSourceCreditCard: Yup.boolean(),
  }

  const getValidationSchema = () => {
    if (isNewSource) {
      if (isSourceCard) {
        return cardValidationSchema;
      } else {
        return bankValidationSchema;
      }
    } else {
      return existingPaymentSchema;
    }
  }

  let validationSchema = Yup.object(getValidationSchema());

  useEffect(() => {
    // eslint-disable-next-line
    validationSchema = Yup.object(getValidationSchema());
  }, [dispatch, isSourceCard, isNewSource, formRef?.current?.values?.makePaymentType])

  useEffect(() => {
    if (!open && (stripeToken || stripeBankToken)) { // cleanup token state
      dispatch(setPatientMakePaymentToken({}))
    }
  }, [dispatch, open, stripeToken, stripeBankToken])

  useEffect(() => {
    checkIfIsValid(formRef?.current?.values)
  // eslint-disable-next-line
  }, [stripeToken, stripeBankToken, open])
  interface PaymentProgressShape {
    makePaymentType: string,
    amount: number | null,
    isNewSourceCreditCard: boolean,
    paymentMethod: string,
    nameOnCard?: string,
    bankName?: string,
    routingNumber?: string,
    accountNumber?: string,
    retypeAccountNumber?: string
  }

  function checkIfIsValid(value: PaymentProgressShape) {
    if ((!isSourceCard) && stripeToken) {
      dispatch(setPatientMakePaymentToken({})) // need to reset stripe token each time we change 'use existing source'
    }
    setIsNewSource(value?.paymentMethod === 'newSource')
    setIsSourceCard(value?.isNewSourceCreditCard)

    validationSchema
      .validate(value)
      .then(() => {
        if ((value?.makePaymentType === 'other') && ((!value?.amount))) {
          setEnableSaveButton(false);
        } else {
          setEnableSaveButton(true);
        }
      })
      .catch((err) => {
        setEnableSaveButton(false);
      });
  }

  const paymentMethodListItem = (paymentMethod: StripePaymentMethod, formik: CustomFormikProps, index: number) => {
    const isCard = paymentMethod?.object === "card"
    return <>
      <Grid
        container
        className="item"
        direction="row"
        justifyContent="space-around"
        alignItems="center"
      >
        <Grid item xs={6} className="pt-1 pl-1">
          <Box justifyContent="flex-start">
            <Radio
              id={index.toString()}
              name="paymentMethod"
              label={isCard ? `${paymentMethod?.brand} ${paymentMethod?.name}` : `ACH ${paymentMethod?.name || ''}`} // Brand and Full Name
              value={index.toString()}
              checked={index.toString() === formik.values['paymentMethod']}
              onChange={formik.handleChange}
            />
          </Box>
        </Grid>
        <Grid item xs={6} >
          <Box display="flex" justifyContent="flex-end" className="mt-2">
            <Typography variant="h4">
              {isCard ? `●●●● ●●●● ●●●● ${paymentMethod?.last4}` : `Routing ●●●●●${paymentMethod?.last4}`}
            </Typography>
          </Box>
        </Grid>
      </Grid>
      <Grid>
        <Divider />
      </Grid>
    </>;
  }

  return (
    <Dialog
      scroll="body"
      className="modal client-modal"
      open={open}
      fullWidth={true}
      maxWidth="md"
    >
      <DialogTitle>
        <span className="title">Make a One-Time Payment</span>
      </DialogTitle>
      <DialogContent>
        <Formik
          enableReinitialize
          innerRef={formRef}
          initialValues={initialValues}
          validationSchema={validationSchema}
          validate={checkIfIsValid}
          onSubmit={() => { }}
        >
          {(formik) => (
            <Form>
              <Grid container spacing={2} className="icon-colors">
                <Grid item xs={12} className="mt-2">
                  <Typography variant="subtitle2">
                    Payment Amount
                  </Typography>
                </Grid>
                <Grid container direction="column" className="pr-1">
                  <Grid
                    container
                    className="item"
                    direction="row"
                    justifyContent="space-around"
                    alignItems="center"
                  >
                    <Grid item xs={6} className="pt-1 pl-1">
                      <Box justifyContent="flex-start">
                        <Radio
                          id="missedPayment"
                          name="makePaymentType"
                          label={(patientPaymentProgram?.patientPaymentSchedule?.missedPmts > 0) ? "Missed Payment" : "Next Payment"}
                          value="missedPayment"
                          checked={"missedPayment" === formik.values['makePaymentType']}
                          onChange={formik.handleChange}
                        />
                      </Box>
                      {patientPaymentProgram?.patientPaymentSchedule?.missedPmts > 0 && (
                        <Box justifyContent="flex-start" className="ml-2 pl-8">
                          <Typography variant="caption">
                            {`${patientPaymentProgram?.patientPaymentSchedule?.lastPaymentFailureDt} (${formatNumberToUSD(nextOrMissedPaymentValue)})`}
                            </Typography>
                        </Box>
                      )}
                    </Grid>
                    <Grid item xs={6}>
                      <Box display="flex" justifyContent="flex-end">
                        <Typography variant="subtitle2">
                          {`${formatNumberToUSD(nextOrMissedPaymentValue)}`}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                  <Grid  >
                    <RadioListItem
                      id="balance"
                      name="makePaymentType"
                      radioLabel="Balance"
                      value="balance"
                      checked={"balance" === formik.values['makePaymentType']}
                      onChange={formik.handleChange}
                      endOfRowLabel={`${formatNumberToUSD(patientPaymentProgram?.patientPaymentSchedule?.pfrBalance)}`}
                    />
                  </Grid>
                  <Grid
                    container
                    className="item pb-3"
                    direction="row"
                    justifyContent="space-around"
                    alignItems="center"
                  >
                    <Grid item xs={6} className="pt-1 pl-1">
                      <Box justifyContent="flex-start">
                        <Radio
                          id="other"
                          name="makePaymentType"
                          label="Other"
                          value="other"
                          checked={"other" === formik.values['makePaymentType']}
                          onChange={formik.handleChange}
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box display="flex" justifyContent="flex-end">
                        <TextField
                          error={
                            formik.touched["amount"] && formik.errors["amount"]
                          }
                          startAdornment={<AttachMoneyIcon />}
                          name="amount"
                          value={formik.values.amount}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          disabled={formik.values.makePaymentType !== 'other'}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                  <Grid>
                    <Divider />
                  </Grid>
                  {patientPaymentMethods && patientPaymentMethods?.length > 0 && patientPaymentMethods?.map((paymentMethod: StripePaymentMethod, index: number) => (
                      paymentMethodListItem(paymentMethod, formik, index)
                    ))
                  }
                  <Grid
                    container
                    className="item"
                    direction="row"
                    justifyContent="space-around"
                    alignItems="center"
                  >
                    <Grid item xs={12} className="pt-1 pl-1">
                      <Box justifyContent="flex-start">
                        <Radio
                          id="newSource"
                          name="paymentMethod"
                          label="New Source"
                          value="newSource"
                          checked={"newSource" === formik.values['paymentMethod']}
                          onChange={formik.handleChange}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                </Grid>
                {(formik.values.paymentMethod === 'newSource') && (
                  <>
                    {/* <Grid item xs={12}>  disabling ACH for now; ACH requires verification so it can't be used as a one time payment.
                      <Toggle
                        name="isNewSourceCreditCard"
                        leftText="Credit Card"
                        rightText="ACH"
                        value={formik.values.isNewSourceCreditCard}
                        formik={formik}
                      />
                    </Grid> */}
                    {(formik.values.isNewSourceCreditCard) ? (
                      <>
                      <Grid item xs={12}>
                        <TextField
                          error={
                            formik.touched["nameOnCard"] && formik.errors["nameOnCard"]
                          }
                          label="Name on Card"
                          placeholder="Enter Name on Card"
                          name="nameOnCard"
                          value={formik.values.nameOnCard}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        {!isFullPayPlan && (
                          <Checkbox
                            checked={formik.values.saveAsRecurring}
                            id='saveAsRecurring'
                            label='Use For Future Payments'
                            name="saveAsRecurring"
                            onChange={formik.handleChange}
                          />
                        )}
                      </Grid>
                      <Grid item xs={12} className="my-2">
                        <StripeCardField
                          disabledCallback={stripeDisabledCallback}
                          tokenCallback={stripeTokenCallback}
                          recurringPaymentTokenCallback={stripeRecurringPaymentTokenCallback}
                          recurringPayment={formik.values.saveAsRecurring && !isFullPayPlan}
                          data={
                            {
                              name: formik.values.nameOnCard,
                              address_country: 'US'
                            }
                          }
                        />
                      </Grid>
                    </>
                    ) : (
                      <>
                        <Grid item xs={6}>
                          <TextField
                            error={
                              formik.touched["bankName"] && formik.errors["bankName"]
                            }
                            label="Bank Name"
                            name="bankName"
                            value={formik.values.bankName}
                            placeholder="Enter Bank Name"
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <TextField
                            error={
                              formik.touched["routingNumber"] && formik.errors["routingNumber"]
                            }
                            label="Routing Number"
                            name="routingNumber"
                            value={formik.values.routingNumber}
                            placeholder="Enter Routing Number"
                            maxLength={9}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <TextField
                            error={
                              formik.touched["accountNumber"] && formik.errors["accountNumber"]
                            }
                            label="Account Number"
                            name="accountNumber"
                            value={formik.values.accountNumber}
                            placeholder="Enter Account Number"
                            maxLength={17}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <TextField
                            error={
                              formik.touched["retypeAccountNumber"] && formik.errors["retypeAccountNumber"]
                            }
                            label="Re-Enter Account Number"
                            name="retypeAccountNumber"
                            value={formik.values.retypeAccountNumber}
                            placeholder="Re Enter Account Number"
                            maxLength={17}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <StripeBankField
                            tokenCallback={stripeTokenCallback}
                            bankData={
                              {
                                country: 'US',
                                currency: 'usd',
                                routing_number: formik.values.routingNumber,
                                account_number: (formik.values.accountNumber === formik.values.retypeAccountNumber) ? formik.values.retypeAccountNumber : '',
                                bank_name: formik.values.bankName,
                                account_holder_name: 'hardcoded name',
                                account_holder_type: 'individual',
                              }
                            }
                          />
                        </Grid>
                      </ >
                    )}
                  </>
                )}
              </Grid>
            </Form>
          )}
        </Formik>
      </DialogContent>
      <DialogActions>
        <DialogActionButton
          isEnabled={enableSaveButton && ((isSourceCard && isNewSource)
            ? ( isNewSource && !isStripeEmpty && (!!stripeToken))
            : (isNewSource && !isSourceCard) ? (!!stripeBankToken) : (true))}
          savebuttonText='Pay'
          saveStatus={saveCallbackStatus.none}
          executeSave={handleSave}
          handleCallbackSave={handleSaveCallback}
          handleCallbackCancel={handleModalClose}
        />
      </DialogActions>
    </Dialog>
  );
}
